import React, { useState, useEffect, useMemo, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  CalendarDays,
  Star,
  CheckCircle2,
  Link,
  Check,
  Users,
  X,
  Tag as TagIcon,
  Plus,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "react-toastify";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Checkbox } from "../../components/ui/checkbox";
import { cn } from "../../lib/utils";
import { getPublicFileUrl } from "../../config";
import {
  createItem,
  updateItem,
} from "../../services/tarefas";
import { touchTask } from "../../services/recentContext";

const TZ_BR = "America/Sao_Paulo";
const formatBrDate = (iso, withYear = true) => {
  if (!iso) return "";
  try {
    const d = new Date(String(iso));
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit",
      ...(withYear ? { year: "numeric" } : {}),
      timeZone: TZ_BR,
    });
  } catch { return String(iso); }
};
const formatBrTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(String(iso));
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: TZ_BR });
  } catch { return String(iso); }
};
const formatBrDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(String(iso));
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
      timeZone: TZ_BR,
    });
  } catch { return String(iso); }
};

import { Progress } from "../../components/ui/progress";

const initialsOf = (name) => {
  const s = (name || "").trim().split(/\s+/).filter(Boolean);
  if (s.length === 0) return "?";
  if (s.length === 1) return s[0].slice(0, 2).toUpperCase();
  return (s[0][0] + s[s.length - 1][0]).toUpperCase();
};

const simpleHash = (s) => {
  let h = 2166136261;
  const str = String(s || "?");
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0);
};

const colorForName = (s) => {
  const palette = [
    "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16",
    "#22c55e", "#10b981", "#14b8a6", "#06b6d4", "#0ea5e9",
    "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7", "#d946ef",
    "#ec4899", "#f43f5e",
  ];
  return palette[simpleHash(s) % palette.length];
};

const identiconSvgUrl = (name) => {
  const n = String(name || "?").trim() || "?";
  const bg = colorForName(n);
  const text = initialsOf(n);
  const w = 80;
  const h = 80;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
    `<rect width="${w}" height="${h}" fill="${bg}"/>` +
    `<text x="50%" y="52%" dominant-baseline="middle" text-anchor="middle" ` +
    `fill="#ffffff" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" ` +
    `font-weight="700" font-size="30">${text}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const avatarSrcOf = (u) => {
  if (!u) return undefined;
  const raw = u.profilePicUrl || u.avatarUrl || u.avatar || u.picUrl || undefined;
  if (!raw) return identiconSvgUrl(u?.name || `user-${u?.id || "?"}`);
  if (typeof raw !== "string") return identiconSvgUrl(u?.name || `user-${u?.id || "?"}`);
  const resolved = getPublicFileUrl(raw) || raw;
  if (!resolved) return identiconSvgUrl(u?.name || `user-${u?.id || "?"}`);
  return resolved;
};

const deduplicateStatuses = (arr) => {
  const byName = new Map();
  const list = Array.isArray(arr) ? arr : [];
  for (const s of list) {
    const key = String(s.name || "Sem status").trim();
    if (!key) continue;
    if (!byName.has(key)) byName.set(key, s);
  }
  return Array.from(byName.values()).sort(
    (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0)
  );
};

const dbToStars = (p) => {
  switch (Number(p)) {
    case 1:
      return 2;
    case 2:
      return 4;
    case 3:
      return 5;
    default:
      return 0;
  }
};

const starsToDb = (n) => {
  const x = Math.max(0, Math.min(5, Number(n) || 0));
  if (x <= 0) return 0;
  if (x <= 2) return 1;
  if (x <= 4) return 2;
  return 3;
};

function SeletorEstrelas({ valor, onChange, tamanho = 22 }) {
  const [hover, setHover] = useState(0);
  const exibido = hover > 0 ? hover : valor;

  const stars = useMemo(
    () =>
      [0, 1, 2, 3, 4].map((i) => {
        const filled = i < exibido;
        return (
          <button
            type="button"
            key={i}
            onClick={() => {
              const novoValor = filled && valor === i + 1 ? 0 : i + 1;
              onChange(novoValor);
            }}
            onMouseEnter={() => setHover(i + 1)}
            onMouseLeave={() => setHover(0)}
            onFocus={() => setHover(i + 1)}
            onBlur={() => setHover(0)}
            aria-label={`Prioridade ${i + 1}`}
            className={cn(
              "inline-flex items-center justify-center p-0.5 -mx-0.5 rounded transition-transform hover:scale-110 active:scale-95"
            )}
          >
            <Star
              style={{ width: tamanho, height: tamanho }}
              className={cn(
                "shrink-0 transition-colors",
                filled
                  ? "fill-amber-400 text-amber-400"
                  : "fill-transparent text-slate-300 hover:text-amber-300"
              )}
            />
          </button>
        );
      }),
    [exibido, valor, onChange, tamanho]
  );

  return <div className="inline-flex items-center">{stars}</div>;
}

const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(203, 213, 225, ${alpha})`;
  const h = hex.replace("#", "");
  if (h.length !== 6) return `rgba(203, 213, 225, ${alpha})`;
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${alpha})`;
};

function ItemEditModal({
  open,
  onClose,
  initialValues,
  defaultLaneId,
  lanesCatalogo = [],
  statusesCatalogo = [],
  usersCatalogo = [],
  tagsCatalogo = [],
  onSave,
  onRefresh,
}) {
  const { user: authUser } = useContext(AuthContext);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priorityDb, setPriorityDb] = useState(0);
  const [dueDate, setDueDate] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [sharedIds, setSharedIds] = useState([]);
  const [laneId, setLaneId] = useState("");
  const [statusId, setStatusId] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [progressPct, setProgressPct] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const stars = dbToStars(priorityDb);

  // Unifica lista de usuários do catálogo (usuários ativos) com o DONO (user) e o
  // ASSIGNEE da tarefa. Isso garante que o Select de Responsável e os checkboxes
  // de compartilhamento SEMPRE terão a opção correspondente, mesmo que a API /users
  // não retorne um desses usuários (ex.: inativo ou bug em filtros de empresa).
  const usersCatalogoFinal = useMemo(() => {
    const merged = new Map();
    const push = (u) => {
      if (!u || !u.id) return;
      const key = String(u.id);
      if (!merged.has(key)) merged.set(key, u);
    };
    (usersCatalogo || []).forEach(push);
    if (initialValues?.user) push(initialValues.user);
    if (initialValues?.assignee) push(initialValues.assignee);
    if (authUser) push(authUser);
    return Array.from(merged.values());
  }, [usersCatalogo, initialValues?.user, initialValues?.assignee, authUser]);

  const statusesDedup = useMemo(
    () => deduplicateStatuses(statusesCatalogo),
    [statusesCatalogo]
  );

  useEffect(() => {
    if (open) {
      setTitle(initialValues?.title || "");
      setDescription(initialValues?.description || "");
      setPriorityDb(Number(initialValues?.priority || 0));
      const d = initialValues?.dueDate ? String(initialValues.dueDate).slice(0, 10) : "";
      setDueDate(d);
      const ids = Array.isArray(initialValues?.tags)
        ? initialValues.tags.map((t) => String(t.id ?? t))
        : [];
      setSelectedTags(ids);
      const sharesRaw = Array.isArray(initialValues?.shares)
        ? initialValues.shares
            .map((s) => s?.user?.id ?? s?.userId)
            .filter((x) => x !== null && x !== undefined)
            .map(String)
        : [];
      if (sharesRaw.length === 0 && Array.isArray(initialValues?.sharedUserIds)) {
        sharesRaw.push(...initialValues.sharedUserIds.map(String));
      }
      // Dono e responsável já visualizam a tarefa e são incluídos automaticamente
      // no backend — NÃO devem aparecer marcados em "Compartilhado com".
      //
      // REGRAS DO "RESPONSÁVEL EFETIVO" (unifica tabela + modal):
      // 1) Se houver assigneeId (responsável atribuído) → usa ele.
      // 2) Senão, e houver dono da tarefa (userId) → DONO é o responsável padrão (mostra ele).
      // 3) Senão, estiver EDITANDO tarefa antiga SEM userId (NULL no banco por ser criada antes da migration) → usa USUÁRIO LOGADO como fallback para não ficar "Não atribuído".
      // 4) Senão, estiver CRIANDO tarefa nova → o usuário logado (authUser) é o responsável padrão.
      // 5) Em "Compartilhado com" → sempre remove (não marca) owner + effectiveAssignee.
      const itemOwnerIdRaw = initialValues?.userId ?? initialValues?.user?.id ?? null;
      const rawAssigneeId = initialValues?.assignee?.id ?? initialValues?.assigneeId ?? null;
      const isEdit = Boolean(initialValues?.id);
      let effectiveAssigneeId = rawAssigneeId;
      if (effectiveAssigneeId === null || effectiveAssigneeId === undefined || Number(effectiveAssigneeId) === 0) {
        if (itemOwnerIdRaw) {
          effectiveAssigneeId = itemOwnerIdRaw;
        } else if (isEdit && authUser?.id) {
          // Fallback para tarefas ANTIGAS (sem userId no banco) — o usuário que está editando é o dono padrão.
          effectiveAssigneeId = authUser.id;
        } else if (!isEdit && authUser?.id) {
          effectiveAssigneeId = authUser.id;
        }
      }
      const itemOwnerId = itemOwnerIdRaw ?? authUser?.id ?? effectiveAssigneeId ?? null;
      const excluidos = new Set(
        [itemOwnerId, effectiveAssigneeId]
          .filter((x) => x !== null && x !== undefined)
          .map((x) => String(x))
      );
      const shares = sharesRaw.filter((sid) => !excluidos.has(sid));
      setSharedIds(shares);
      const lid = initialValues?.laneId ?? defaultLaneId ?? "";
      setLaneId(lid === null || lid === undefined ? "" : String(lid));
      const sid = initialValues?.status?.id ?? initialValues?.statusId;
      setStatusId(sid === null || sid === undefined ? "" : String(sid));
      setAssigneeId(effectiveAssigneeId === null || effectiveAssigneeId === undefined ? "" : String(effectiveAssigneeId));
      const pp = initialValues?.progressPct;
      setProgressPct(pp === null || pp === undefined ? 0 : Math.max(0, Math.min(100, Number(pp) || 0)));
    }
  }, [open, initialValues, defaultLaneId, authUser?.id]);

  const toggleTag = (id) => {
    const sid = String(id);
    setSelectedTags((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  const toggleShare = (id) => {
    const sid = String(id);
    setSharedIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  const handleSalvar = async () => {
    if (!title.trim()) return;
    try {
      setSalvando(true);
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        priority: Number(priorityDb),
        dueDate: dueDate || null,
        tagIds: selectedTags,
        sharedIds: sharedIds,
        laneId: laneId ? Number(laneId) : (initialValues?.laneId ?? defaultLaneId ?? null),
        statusId: statusId ? Number(statusId) : null,
        assigneeId: assigneeId ? Number(assigneeId) : null,
        progressPct: Math.max(0, Math.min(100, Number(progressPct) || 0)),
      };
      const salvo = await onSave(payload);
      const idTarefa = Number(salvo?.id || initialValues?.id || 0);
      if (idTarefa) {
        touchTask({
          id: idTarefa,
          title: payload.title,
          status: statusId ? String(statusId) : null,
          dueDate: payload.dueDate,
          path: `/tarefas#item-${idTarefa}`,
        });
      }
      try { onRefresh?.(); } catch (e) {}
      try { onClose?.(); } catch (e) {}
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialValues?.id ? "Editar tarefa" : "Nova tarefa"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="item-title">Título</Label>
              <Input
                id="item-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex.: Ligar para o cliente João"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label>Grupo (coluna)</Label>
              <Select value={laneId} onValueChange={setLaneId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Selecione um grupo" />
                </SelectTrigger>
                <SelectContent>
                  {lanesCatalogo.map((l) => {
                    const displayName =
                      l.name && l.name.length > 0
                        ? l.name.charAt(0).toUpperCase() + l.name.slice(1).toLowerCase()
                        : `Coluna ${l.id}`;
                    return (
                      <SelectItem key={l.id} value={String(l.id)}>
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: l.color || "#94a3b8" }}
                          />
                          <span className="truncate">{displayName}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label>Responsável / Compartilhar com</Label>
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <p className="text-[11px] text-muted-foreground -mt-1 mb-1">
                Selecione um usuário para atribuir e compartilhar esta tarefa.
              </p>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Não atribuído" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
                        —
                      </span>
                      Não atribuído
                    </div>
                  </SelectItem>
                  {usersCatalogoFinal.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-5 w-5 shrink-0 border border-border">
                          <AvatarImage src={avatarSrcOf(u)} alt={u.name || ""} />
                          <AvatarFallback className="text-[9px] font-semibold bg-muted text-muted-foreground">
                            {initialsOf(u?.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">{u?.name || `Usuário ${u.id}`}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={statusId} onValueChange={setStatusId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Selecione um status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span
                        className="inline-block h-3 w-3 rounded-full shrink-0 border border-border"
                        style={{ backgroundColor: "#cbd5e1" }}
                      />
                      Sem status
                    </div>
                  </SelectItem>
                  {statusesDedup.map((st) => (
                    <SelectItem key={st.id} value={String(st.id)}>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 rounded-full shrink-0 border border-black/10"
                          style={{ backgroundColor: st.color }}
                        />
                        <span>{st.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="item-progress">
                Progresso ({progressPct}%)
              </Label>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Input
                    id="item-progress"
                    type="number"
                    min={0}
                    max={100}
                    value={progressPct}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (Number.isNaN(v)) setProgressPct(0);
                      else setProgressPct(Math.max(0, Math.min(100, v)));
                    }}
                    className="h-9 w-20 shrink-0 tabular-nums"
                  />
                  <Input
                    type="range"
                    min={0}
                    max={100}
                    value={progressPct}
                    onChange={(e) =>
                      setProgressPct(Math.max(0, Math.min(100, Number(e.target.value))))
                    }
                    className="h-9 flex-1 accent-emerald-600"
                  />
                </div>
                <Progress
                  value={progressPct}
                  overrideBarColor="#10b981"
                  overrideTrackColor="rgba(16, 185, 129, 0.14)"
                  className="h-1.5 mt-0.5"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="item-desc">Descrição</Label>
            <Textarea
              id="item-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Detalhes adicionais..."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Prioridade</Label>
              <div className="flex items-center gap-3 rounded-md border border-border bg-background p-2.5">
                <SeletorEstrelas
                  valor={stars}
                  onChange={(n) => setPriorityDb(starsToDb(n))}
                  tamanho={20}
                />
                <span className="text-xs text-muted-foreground ml-1">
                  {stars === 0 && "Sem prioridade"}
                  {stars > 0 && stars <= 2 && "Baixa"}
                  {stars > 2 && stars <= 4 && "Média"}
                  {stars === 5 && "Alta"}
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="item-date">Data de vencimento</Label>
              <Input
                id="item-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="h-9"
              />
            </div>
          </div>

          {Array.isArray(tagsCatalogo) && tagsCatalogo.length > 0 && (
            <div className="space-y-2">
              <Label>Etiquetas</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[220px] overflow-y-auto border border-border rounded-md p-3">
                {tagsCatalogo.map((t) => {
                  const sid = String(t.id);
                  const checked = selectedTags.includes(sid);
                  return (
                    <div
                      key={sid}
                      className="flex items-center gap-2 rounded-md p-1.5 hover:bg-accent/40"
                    >
                      <Checkbox
                        id={`tag-${sid}`}
                        checked={checked}
                        onCheckedChange={() => toggleTag(t.id)}
                      />
                      <div
                        className="h-3 w-3 rounded-full shrink-0 border"
                        style={{ backgroundColor: t.color || "#cbd5e1", borderColor: t.color || "#cbd5e1" }}
                      />
                      <label
                        htmlFor={`tag-${sid}`}
                        className="text-sm cursor-pointer flex-1 truncate select-none"
                      >
                        {t.name}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {Array.isArray(usersCatalogo) && usersCatalogo.length > 0 && (
            <div className="space-y-2 border-t border-border/70 pt-3">
              <div className="flex items-center gap-1.5">
                <Label>Compartilhado com</Label>
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <p className="text-[11px] text-muted-foreground -mt-1">
                Marque outros usuários para que eles também vejam e possam gerenciar esta tarefa.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[260px] overflow-y-auto border border-border rounded-md p-3 bg-card/30">
                {usersCatalogoFinal.map((u) => {
                  const sid = String(u.id);
                  const itemOwnerId = initialValues?.userId ?? initialValues?.user?.id ?? authUser?.id ?? null;
                  const isOwner = String(itemOwnerId || "") === sid;
                  const isAssignee = String(assigneeId || "") === sid;
                  // Proteção dupla: mesmo que o array sharedIds tenha (dados antigos),
                  // NUNCA marca o checkbox do proprietário ou do responsável.
                  const checked = sharedIds.includes(sid) && !isAssignee && !isOwner;
                  return (
                    <div
                      key={sid}
                      className={cn(
                        "flex items-center gap-2 rounded-md p-1.5 hover:bg-accent/40",
                        (isAssignee || isOwner) && !checked && "bg-emerald-500/10 dark:bg-emerald-500/15"
                      )}
                    >
                      <Checkbox
                        id={`share-${sid}`}
                        checked={checked}
                        onCheckedChange={() => {
                          if (isAssignee || isOwner) return;
                          toggleShare(u.id);
                        }}
                        disabled={isAssignee || isOwner}
                      />
                      <Avatar className="h-5 w-5 shrink-0 border border-border">
                        <AvatarImage src={avatarSrcOf(u)} alt={u.name || ""} />
                        <AvatarFallback className="text-[9px] font-semibold bg-muted text-muted-foreground">
                          {initialsOf(u?.name)}
                        </AvatarFallback>
                      </Avatar>
                      <label
                        htmlFor={`share-${sid}`}
                        className={cn(
                          "text-sm cursor-pointer flex-1 truncate select-none flex items-center gap-2",
                          (isAssignee || isOwner) && "pointer-events-none"
                        )}
                      >
                        <span className="truncate">{u?.name || `Usuário ${sid}`}</span>
                        {isAssignee && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-emerald-400/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 dark:bg-emerald-500/15">
                            Responsável
                          </Badge>
                        )}
                        {!isAssignee && isOwner && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-emerald-400/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 dark:bg-emerald-500/15">
                            Proprietário
                          </Badge>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
              {sharedIds.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  Compartilhado com <strong className="text-foreground/80">{sharedIds.length}</strong> usuário(s) além do responsável.
                </p>
              )}

              {initialValues?.id && (
                <div className="border-t border-border/70 pt-3 mt-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-foreground/90">Link direto da tarefa</div>
                      <div className="text-[11px] text-muted-foreground">
                        Copie e envie o link para acessar esta tarefa diretamente.
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={copiedLink ? "default" : "outline"}
                      className={cn(
                        "h-8 px-3 gap-1.5",
                        copiedLink && "bg-emerald-600 hover:bg-emerald-600 text-white border-emerald-600"
                      )}
                      onClick={async () => {
                        const url = `${window.location.origin}/tarefas/item/${encodeURIComponent(initialValues.id)}`;
                        try {
                          if (navigator.clipboard && navigator.clipboard.writeText) {
                            await navigator.clipboard.writeText(url);
                          } else {
                            const ta = document.createElement("textarea");
                            ta.value = url;
                            ta.style.position = "fixed";
                            ta.style.opacity = "0";
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand("copy");
                            document.body.removeChild(ta);
                          }
                          setCopiedLink(true);
                          toast.success("Link da tarefa copiado");
                          setTimeout(() => setCopiedLink(false), 2000);
                        } catch (err) {
                          console.error(err);
                          toast.error("Não foi possível copiar o link");
                        }
                      }}
                    >
                      {copiedLink ? (
                        <>
                          <Check className="h-3.5 w-3.5" /> Copiado
                        </>
                      ) : (
                        <>
                          <Link className="h-3.5 w-3.5" /> Copiar link
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {initialValues?.completedAt && (
                <div className="border-t border-border/70 pt-3 mt-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-foreground/90 flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
                        Concluída em
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        Data e hora em horário de Brasília (UTC-3).
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className="text-sm font-medium text-emerald-700 dark:text-emerald-400 tabular-nums"
                        title={formatBrDateTime(initialValues.completedAt)}
                      >
                        {formatBrDate(initialValues.completedAt, true)}
                      </div>
                      <div className="text-[11px] text-muted-foreground tabular-nums">
                        {formatBrTime(initialValues.completedAt)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button type="button" variant="ghost" onClick={() => onClose?.()} disabled={salvando}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSalvar} disabled={salvando || !title.trim()}>
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ItemEditModal;
